import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, usePageMeta } from "@/components/site/Reveal";
import { CONTACT, SUBJECT_OPTIONS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const initialForm = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  usePageMeta(
    "Contact — SKN Group of Companies | Kolkata Head Office",
    "Contact SKN Group: 1, Ripon Street, Kolkata 700016, India. Phone 1800 12000 1400 (toll-free), email headoffice@skngroup.net."
  );

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!form.email.trim()) errs.email = "Please enter your email.";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.subject) errs.subject = "Please select a subject.";
    if (!form.message.trim()) errs.message = "Please enter a message.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject,
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Your inquiry has been received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-40 md:pt-48 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">Contact</p>
            <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
              Begin a <span className="italic bronze">conversation</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14">
          {/* Details + map */}
          <div className="md:col-span-5 space-y-10">
            <Reveal>
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin size={20} strokeWidth={1.25} className="bronze mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Head Office</p>
                    <p className="text-base font-medium">{CONTACT.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <Phone size={20} strokeWidth={1.25} className="bronze mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Phone</p>
                    <a href={`tel:${CONTACT.phoneTel}`} className="text-base font-medium hover:text-[#B8997A] transition-colors">
                      {CONTACT.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <Mail size={20} strokeWidth={1.25} className="bronze mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Email</p>
                    <a href={`mailto:${CONTACT.email}`} className="text-base font-medium hover:text-[#B8997A] transition-colors">
                      {CONTACT.email}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Styled static map visual */}
            <Reveal delay={0.15}>
              <div className="map-grid relative h-72 overflow-hidden" data-testid="contact-map-visual">
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-3.5 h-3.5 bg-[#B8997A] rounded-full marker-pulse" />
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between">
                  <div>
                    <p className="text-[#F7F5F0] font-display text-xl">Ripon Street, Kolkata</p>
                    <p className="text-[#F7F5F0]/50 text-xs mt-1 tracking-widest">{CONTACT.coordinates}</p>
                  </div>
                  <p className="text-[#B8997A] text-[10px] uppercase tracking-[0.3em]">West Bengal · IN</p>
                </div>
                <div className="absolute top-5 left-5 text-[#F7F5F0]/30 text-[10px] uppercase tracking-[0.3em]">
                  SKN Group HQ
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="md:col-span-7">
            {submitted ? (
              <Reveal>
                <div
                  className="border border-[#B8997A]/50 p-10 md:p-14 text-center md:text-left"
                  data-testid="contact-success-message"
                >
                  <CheckCircle2 size={40} strokeWidth={1.25} className="bronze mx-auto md:mx-0" />
                  <h2 className="mt-6 font-display text-3xl md:text-4xl font-medium tracking-tight">
                    Thank you. We've received your inquiry.
                  </h2>
                  <p className="mt-4 text-muted-foreground leading-relaxed">
                    Our head office team will get back to you shortly at the email you provided.
                  </p>
                  <button
                    onClick={() => {
                      setForm(initialForm);
                      setSubmitted(false);
                    }}
                    data-testid="contact-send-another-button"
                    className="mt-8 inline-flex items-center gap-2 border border-foreground/30 px-6 py-3 text-sm hover:border-[#B8997A] hover:text-[#B8997A] transition-colors duration-300"
                  >
                    Send Another Inquiry
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={handleSubmit} noValidate className="space-y-7" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em]">
                        Name <span className="bronze">*</span>
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your full name"
                        className="h-12 bg-transparent border-foreground/25 focus-visible:ring-[#B8997A]"
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive" data-testid="contact-error-name">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em]">
                        Email <span className="bronze">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@company.com"
                        className="h-12 bg-transparent border-foreground/25 focus-visible:ring-[#B8997A]"
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive" data-testid="contact-error-email">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em]">
                        Phone <span className="text-muted-foreground normal-case">(optional)</span>
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+91 ..."
                        className="h-12 bg-transparent border-foreground/25 focus-visible:ring-[#B8997A]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em]">
                        Subject <span className="bronze">*</span>
                      </Label>
                      <Select
                        value={form.subject}
                        onValueChange={(v) => {
                          setForm({ ...form, subject: v });
                          if (errors.subject) setErrors({ ...errors, subject: null });
                        }}
                      >
                        <SelectTrigger
                          className="h-12 bg-transparent border-foreground/25 focus:ring-[#B8997A]"
                          data-testid="contact-select-subject"
                        >
                          <SelectValue placeholder="Division of interest" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#F7F5F0]">
                          {SUBJECT_OPTIONS.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`contact-subject-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.subject && (
                        <p className="text-sm text-destructive" data-testid="contact-error-subject">{errors.subject}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em]">
                      Message <span className="bronze">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about your inquiry..."
                      rows={6}
                      className="bg-transparent border-foreground/25 focus-visible:ring-[#B8997A] resize-none"
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive" data-testid="contact-error-message">{errors.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 bg-foreground text-background px-10 py-4 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Sending..." : "Send Inquiry"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
